import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type ChartTypeRegistry } from "chart.js";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import { type ICheckList, type ISection } from "src/interfaces";
import {
  type IReportData,
  type IReportDetails,
  type IYachtData,
} from "src/pages/Reports/Report/Report.types";

export type TReportMode =
  | "all"
  | "entries"
  | "checkLists"
  | "attachments"
  | "entriesAndCheckLists"
  | "entriesAndAttachments";

export interface IChart {
  name: string;
  type: keyof ChartTypeRegistry;
  vals: any[];
  trendline: boolean;
}

export interface ILogbookCharts {
  lbid: string;
  data: IChart[];
}

interface EntriesGeneratorSlice {
  users: IUser[];
  selectingMode: boolean;
  selectedCols: Record<string, Record<number, string[]>>;
  chartsData: ILogbookCharts[];
  logbooks: Array<{ label: string; value: string }>;
  selectedLogbook: string;
  selectedArchivedStatus: string;
  sections: Record<string, ISection[]>;
  yachtdata: IYachtData | null;
  reportDetails: IReportDetails | null;
  checkLists: ICheckList[];
  reportMode: TReportMode;
  emptyEntries: boolean;
}

const initialState: EntriesGeneratorSlice = {
  users: [],
  selectingMode: false,
  selectedCols: {},
  chartsData: [],
  logbooks: [],
  selectedLogbook: "",
  selectedArchivedStatus: "",
  sections: {},
  yachtdata: null,
  reportDetails: null,
  checkLists: [],
  reportMode: "entries",
  emptyEntries: true,
};

export const entriesGeneratorSlice = createSlice({
  name: "entriesGeneratorSlice",
  initialState,
  reducers: {
    setReportData: (state, action: PayloadAction<IReportData>) => {
      state.users = action.payload?.users;
      state.reportDetails = action.payload?.report;
      state.selectingMode = false;
      state.selectedCols = {};

      if (action.payload?.yachtdata) state.yachtdata = action.payload.yachtdata;

      if (
        action.payload?.report?.type === 0 ||
        action.payload?.report?.type === 2
      ) {
        const logbooks: Array<{ label: string; value: string }> = [];
        const sections: Record<string, ISection[]> = {};

        if (typeof action.payload?.result === "string") return;

        action.payload.result.forEach(entry => {
          if (!logbooks.find(log => log.value === entry.lbid)) {
            logbooks.push({ label: entry.lbname, value: entry.lbid });
            sections[entry.lbid] = entry.lbentry.sections;
          }
        });

        logbooks.sort(({ label: a }, { label: b }) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );

        state.logbooks = logbooks;
        state.sections = sections;

        state.selectedLogbook = logbooks[0]?.value;
      }
    },
    setCheckLists: (state, action) => {
      state.checkLists = action.payload;
    },
    setUsers: (state, action: PayloadAction<IUser[]>) => {
      state.users = action.payload;
    },
    updateUserContactNumber: (
      state,
      action: PayloadAction<{ userid: string; contactNumber: string }>,
    ) => {
      const { userid, contactNumber } = action.payload;
      const userIndex = state.users.findIndex(user => user.userid === userid);
      if (userIndex !== -1) {
        const user = state.users[userIndex];
        // Update the field that currently contains the contact number, or mobile if both are empty
        if (user.mobile) {
          // If user has mobile, update mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        } else if (user.phoneno) {
          // If user has phoneno but no mobile, update phoneno
          state.users[userIndex] = {
            ...state.users[userIndex],
            phoneno: contactNumber,
          };
        } else {
          // If user has neither, default to mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        }
      }
    },
    setReportMode: (state, action: PayloadAction<TReportMode>) => {
      state.reportMode = action.payload;
    },
    setSelectedLogbook: (state, action: PayloadAction<string>) => {
      state.selectedLogbook = action.payload;
    },
    setSelectedArchivedStatus: (state, action: PayloadAction<string>) => {
      state.selectedArchivedStatus = action.payload;
    },
    setSelectingMode: (state, action: PayloadAction<boolean>) => {
      state.selectingMode = action.payload;
    },
    toggleSelectingMode: state => {
      state.selectingMode = !state.selectingMode;
    },
    setYachtdata: (state, action) => {
      state.yachtdata = action.payload;
    },
    handleSpec: (
      state,
      action: PayloadAction<{ section: number; col: string }>,
    ) => {
      const selectedLogCols = state.selectedCols[state.selectedLogbook] || {};
      const { section, col } = action.payload;
      if (!selectedLogCols[section]) {
        selectedLogCols[section] = [col];
      } else {
        if (selectedLogCols[section].includes(col)) {
          selectedLogCols[section] = selectedLogCols[section].filter(
            c => c !== col,
          );
        } else {
          selectedLogCols[section].push(col);
        }
      }
      state.selectedCols = {
        ...state.selectedCols,
        [state.selectedLogbook]: selectedLogCols,
      };
    },
    setChartsData: (
      state,
      action: PayloadAction<Array<{ lbid: string; data: IChart[] }>>,
    ) => {
      state.chartsData = action.payload;
      state.selectingMode = false;
    },
    resetCharts: state => {
      state.chartsData = [];
      state.selectedCols = {};
    },

    setIsEmpty: (state, action: PayloadAction<boolean>) => {
      state.emptyEntries = action.payload;
    },
    resetReportData: () => initialState,
  },
});

export const {
  setReportData,
  setReportMode,
  setSelectedLogbook,
  setSelectedArchivedStatus,
  setSelectingMode,
  setCheckLists,
  setUsers,
  updateUserContactNumber,
  toggleSelectingMode,
  handleSpec,
  setChartsData,
  resetCharts,
  resetReportData,
  setYachtdata,
  setIsEmpty,
} = entriesGeneratorSlice.actions;

export default entriesGeneratorSlice.reducer;
